// Shared utils for Review step

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Build payload for /weddings/cart-items (preview=false for add-to-cart)
 * New model (server infers size from composite):
 * - cake: {
 *     include,
 *     compositeKey,                           // e.g. '2T_STANDARD' (chosen in ConfigSuggestionsStep)
 *     styleId | customStyle1TS,               // standard style OR custom estimator (1TS)
 *     tiers: [{ position, flavorId, ... }],   // one per tier; missing flavor = included flavor (server = $0)
 *     customizations, notes
 *   }
 * - desserts: { include, selections: [{patisserieId, quantity}] }
 * - rentals:  { include, items: [...] }
 * - fulfillment: { method, scheduledDate, setupSelected, address{ distanceKm, ... } }
 */
export function buildPayload(state, { preview = false } = {}) {
  const trim = (s) => (typeof s === "string" ? s.trim() : s);

  const customer = {
    firstName: trim(state?.customer?.firstName),
    lastName: trim(state?.customer?.lastName),
    email: trim(state?.customer?.email)?.toLowerCase(),
    phone: trim(state?.customer?.phone) || undefined,
    phoneSecondary: trim(state?.customer?.phoneSecondary) || undefined,
    phoneSecondaryLabel:
      trim(state?.customer?.phoneSecondaryLabel) || undefined,
    marketingOptIn: !!state?.customer?.marketingOptIn,
  };

  const fulfillment = {
    ...(state?.fulfillment || {}),
    // ensure address object exists with expected keys (server tolerant to extras)
    address: {
      line1: trim(state?.fulfillment?.address?.line1) || "",
      line2: trim(state?.fulfillment?.address?.line2) || "",
      city: trim(state?.fulfillment?.address?.city) || "",
      province: trim(state?.fulfillment?.address?.province) || "",
      postalCode: trim(state?.fulfillment?.address?.postalCode) || "",
      notes: trim(state?.fulfillment?.address?.notes) || "",
      geo: state?.fulfillment?.address?.geo || { lat: null, lng: null },
      distanceKm: Number(state?.fulfillment?.address?.distanceKm || 0),
    },
    scheduledDate: trim(state?.fulfillment?.scheduledDate) || "",
    method: state?.fulfillment?.method || "delivery",
    setupSelected: !!state?.fulfillment?.setupSelected,
  };

  // Support standard style OR custom estimator (1TS).
  // We prefer state.cake.customStyle1TS if present; otherwise styleId.
  const customStyle1TS = Number.isFinite(state?.cake?.customStyle1TS)
    ? Number(state.cake.customStyle1TS)
    : // Optional fallback if your custom step stored it elsewhere:
    Number.isFinite(state?.custom?.estimatedStyle1TS)
    ? Number(state.custom.estimatedStyle1TS)
    : null;

  const cake = state?.cake?.include
    ? {
        include: true,
        compositeKey: state?.cake?.compositeKey || null,
        // send one of these; server requires at least one
        ...(Number.isFinite(customStyle1TS)
          ? { customStyle1TS }
          : { styleId: state?.cake?.styleId || null }),
        tiers: Array.isArray(state?.cake?.tiers) ? state.cake.tiers : [],
        customizations: state?.cake?.customizations || {},
        notes: state?.cake?.notes || "",
      }
    : { include: false };

  const dessertSelections = Array.isArray(state?.desserts?.selections)
    ? state.desserts.selections
    : [];
  const desserts = {
    include: !!state?.desserts?.include || dessertSelections.length > 0,
    selections: dessertSelections,
  };

  const rentals = {
    include:
      Array.isArray(state?.rentals?.items) &&
      state.rentals.items.length > 0 &&
      !!state?.rentals?.include,
    items: Array.isArray(state?.rentals?.items) ? state.rentals.items : [],
  };

  const paymentPreference = {
    payNow: state.payment?.preference?.payNow || "deposit50",
    saveCardForAutoCharge: !!state.payment?.preference?.saveCardForAutoCharge,
  };

  return {
    source: state?.source || "wizard",
    channel: state?.channel || "web",
    tz: state?.tz || "America/St_Johns",
    customer,
    fulfillment,
    cake,
    desserts,
    rentals,
    paymentPreference,
    createdFromVersion: 1,
    preview,
  };
}

/**
 * Client-side validation aligned to v1 schema+flow.
 */
export function useValidation(state) {
  const issues = [];

  const hasCake = !!state.cake?.include;
  const hasDess =
    !!state.desserts?.include ||
    (Array.isArray(state.desserts?.selections) &&
      state.desserts.selections.length > 0);
  const hasRentals =
    Array.isArray(state.rentals?.items) &&
    state.rentals.items.some((r) => Number(r.days) > 0);

  if (!hasCake && !hasDess && !hasRentals) {
    issues.push("Add a cake, desserts, or rentals to continue.");
  }

  if (hasCake) {
    const hasStyleId = !!state.cake?.styleId;
    const hasCustom =
      Number.isFinite(state?.cake?.customStyle1TS) ||
      Number.isFinite(state?.custom?.estimatedStyle1TS);
    if (!hasStyleId && !hasCustom) {
      issues.push("Select a cake style or request a custom style estimate.");
    }
    if (!state.cake?.compositeKey)
      issues.push("Cake size/tier composite is missing.");
    const tiers = Array.isArray(state.cake?.tiers) ? state.cake.tiers : [];
    if (!tiers.length || tiers.some((t) => !t?.flavorId)) {
      issues.push("Choose flavors for each tier.");
    }
  }

  if (hasDess) {
    const emptyQty = (state.desserts?.selections || []).some(
      (d) => Number(d.quantity) <= 0
    );
    if (emptyQty) issues.push("All dessert quantities must be positive.");
  }

  if (hasRentals) {
    const badDays = state.rentals.items.some((r) => Number(r.days) <= 0);
    if (badDays) issues.push("All rentals must be at least 1 day.");
  }

  const deliverySelected = state.fulfillment?.method === "delivery";
  const addressConfirmed =
    Number(state.fulfillment?.address?.distanceKm || 0) > 0 &&
    !!state.fulfillment?.address?.line1;

  if (
    (deliverySelected || state.fulfillment?.setupSelected) &&
    !addressConfirmed
  ) {
    issues.push(
      "Please select an event address from suggestions (delivery/setup)."
    );
  }
  if (state.fulfillment?.setupSelected && !state.fulfillment?.scheduledDate) {
    issues.push("Event date is required when setup is selected.");
  }

  return issues;
}
